/**
 * UniversityLogo - visual identity for a university (2026-05-04).
 *
 * Why this exists:
 *   Universities are referenced on six student-facing surfaces but always
 *   as plain text + short_name. Mozambican students recognise their target
 *   university's logo on sight before they recognise the institution's
 *   full legal name. A square 1:1 logo next to the uni name speeds visual
 *   scanning and aligns the product with the editais students already see.
 *
 * Source resolution (in order):
 *   1. `logoUrl` prop - admin override stored on `University.logo_url`,
 *      uploaded via POST /admin/universities/:id/logo. Caller passes it
 *      explicitly so we don't have to know which API shape produced it.
 *   2. Convention path `/uni-logos/{slug}.{png|jpeg}` - bundled defaults
 *      committed to `public/uni-logos/`. The slug is `short_name`
 *      lowercased + accents stripped (NFD, drop combining marks). The
 *      component tries `.png` first, then `.jpeg` on error.
 *   3. Letter-circle fallback - first letter of `short_name` on a
 *      coloured background derived deterministically from the
 *      `short_name` so the same uni always gets the same colour even
 *      when no logo is available.
 *
 * Sizes:
 *   sm - 24px, inline mentions
 *   md - 48px, cards
 *   lg - 80px, detail-page headers
 *
 * Accessibility:
 *   The image uses an alt of the full uni name when supplied (or the
 *   short_name as fallback). The letter-circle has `role="img"` +
 *   `aria-label` so screen readers announce it correctly.
 */

import React, { useState, useEffect } from 'react'

interface Props {
  /** Required. Used both as the slug source for the convention path AND
   *  as the letter source for the fallback. */
  shortName: string
  /** Optional full name. Used as the image `alt` when present (better
   *  for screen readers than the bare short_name). */
  name?:     string
  /** Admin override URL from `University.logo_url`. Takes precedence
   *  over the convention path when set. Pass `null`/`undefined` when
   *  no override exists. */
  logoUrl?:  string | null
  /** Display size. Defaults to `md`. */
  size?:     'sm' | 'md' | 'lg'
  /** Extra classes for the outer wrapper. The component handles its
   *  own square sizing + rounded corners; callers should only pass
   *  spacing classes (mr-2, etc.) here. */
  className?: string
}

// ── Constants ────────────────────────────────────────────────────────────────

const SIZE_PX: Record<NonNullable<Props['size']>, number> = {
  sm: 24,
  md: 48,
  lg: 80,
}

// Letter-circle background palette - six saturated-but-not-clashy
// tones. Same palette every run so a uni always gets the same colour;
// the choice is keyed on a hash of the short_name.
const FALLBACK_COLOURS = [
  'bg-indigo-500 text-white',
  'bg-emerald-500 text-white',
  'bg-amber-500 text-white',
  'bg-rose-500 text-white',
  'bg-sky-500 text-white',
  'bg-violet-500 text-white',
] as const

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Lowercase + NFD-strip accents. Matches the convention used to name
 *  files in public/uni-logos/ (e.g. "UniLúrio" → "unilurio"). The
 *  combining-mark range ̀–ͯ covers every Portuguese accent
 *  we use (acute, grave, circumflex, tilde, cedilla decomposes to
 *  c + ̧). */
function slugify(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
}

/** Stable per-string hash; returns an index into FALLBACK_COLOURS so
 *  the same short_name always gets the same colour. */
function fallbackColourClass(s: string): string {
  let h = 0
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0
  return FALLBACK_COLOURS[Math.abs(h) % FALLBACK_COLOURS.length]
}

/** Convention-path candidates the component tries in order. PNG first
 *  (most logos in the bundled set), JPEG second. SVG is implicitly
 *  supported by the admin-override path; we don't bundle SVGs by
 *  convention because the seed assets are raster. */
function conventionPaths(shortName: string): string[] {
  const slug = slugify(shortName)
  return [`/uni-logos/${slug}.png`, `/uni-logos/${slug}.jpeg`]
}

// ── Component ────────────────────────────────────────────────────────────────

export const UniversityLogo: React.FC<Props> = ({
  shortName, name, logoUrl, size = 'md', className = '',
}) => {
  // Build the candidate list in resolution order. Admin override (if
  // any) sits at index 0, then the convention paths. We walk this list
  // by incrementing the candidate index on every <img onError>.
  const candidates = React.useMemo(() => {
    const out: string[] = []
    if (logoUrl) out.push(logoUrl)
    out.push(...conventionPaths(shortName))
    return out
  }, [logoUrl, shortName])

  const [idx, setIdx] = useState(0)

  // Reset to the first candidate whenever the short_name or override
  // changes (e.g. an admin replaces a logo via the upload endpoint).
  useEffect(() => { setIdx(0) }, [logoUrl, shortName])

  const px       = SIZE_PX[size]
  const showImg  = idx < candidates.length
  const altText  = name ?? shortName
  const fallback = fallbackColourClass(shortName)

  // Tailwind text-size mapping - kept proportional to the box so the
  // letter centres cleanly without manual tweaking per size.
  const fontClass =
    size === 'sm' ? 'text-[11px]'  :
    size === 'md' ? 'text-base'    :
                    'text-2xl'

  return (
    <span
      className={`inline-flex items-center justify-center overflow-hidden rounded-lg shrink-0 ${className}`}
      style={{ width: px, height: px }}
    >
      {showImg ? (
        <img
          src={candidates[idx]}
          alt={altText}
          width={px}
          height={px}
          className="w-full h-full object-contain bg-white"
          onError={() => setIdx(i => i + 1)}
          // Workbox precache covers everything in /public - these load
          // even when the student is offline.
          loading="lazy"
        />
      ) : (
        <span
          role="img"
          aria-label={altText}
          className={`w-full h-full flex items-center justify-center font-semibold ${fontClass} ${fallback}`}
        >
          {shortName.charAt(0).toUpperCase()}
        </span>
      )}
    </span>
  )
}

export default UniversityLogo
